import { useState, useCallback } from "react";
import { validateEmail, validatePhone, validateRequired } from "@/utils/validation";

export interface FormField {
    value: string;
    touched: boolean;
    error: string;
}

export interface FormState {
    [key: string]: FormField;
}

export function useFormValidation(initialState: Record<string, string>) {
    const [formState, setFormState] = useState<FormState>(() => {
        const state: FormState = {};
        Object.keys(initialState).forEach((key) => {
            state[key] = {
                value: initialState[key],
                touched: false,
                error: "",
            };
        });
        return state;
    });

    const setValue = useCallback((field: string, value: string) => {
        setFormState((prev) => ({
            ...prev,
            [field]: {
                ...prev[field],
                value,
                error: "", // Clear error when user types
            },
        }));
    }, []);

    const setTouched = useCallback((field: string) => {
        setFormState((prev) => ({
            ...prev,
            [field]: {
                ...prev[field],
                touched: true,
            },
        }));
    }, []);

    const validateField = useCallback(
        (field: string, validationType: "required" | "email" | "phone", fieldLabel: string) => {
            setFormState((prev) => {
                const fieldState = prev[field];
                let error = "";

                if (!fieldState.touched) {
                    return prev;
                }

                switch (validationType) {
                    case "required":
                        if (!validateRequired(fieldState.value)) {
                            error = `${fieldLabel} is required`;
                        }
                        break;
                    case "email":
                        if (!validateRequired(fieldState.value)) {
                            error = `${fieldLabel} is required`;
                        } else if (!validateEmail(fieldState.value)) {
                            error = "Please enter a valid email address";
                        }
                        break;
                    case "phone":
                        if (!validateRequired(fieldState.value)) {
                            error = `${fieldLabel} is required`;
                        } else if (!validatePhone(fieldState.value)) {
                            error = "Please enter a valid phone number";
                        }
                        break;
                }

                return {
                    ...prev,
                    [field]: {
                        ...fieldState,
                        error,
                    },
                };
            });
        },
        []
    );

    const validateAll = useCallback(
        (validations: Record<string, { type: "required" | "email" | "phone"; label: string }>) => {
            let isValid = true;

            setFormState((prev) => {
                const newState = { ...prev };

                Object.entries(validations).forEach(([field, validation]) => {
                    const fieldState = newState[field];
                    let error = "";

                    // Mark as touched
                    newState[field] = { ...fieldState, touched: true };

                    switch (validation.type) {
                        case "required":
                            if (!validateRequired(fieldState.value)) {
                                error = `${validation.label} is required`;
                                isValid = false;
                            }
                            break;
                        case "email":
                            if (!validateRequired(fieldState.value)) {
                                error = `${validation.label} is required`;
                                isValid = false;
                            } else if (!validateEmail(fieldState.value)) {
                                error = "Please enter a valid email address";
                                isValid = false;
                            }
                            break;
                        case "phone":
                            if (!validateRequired(fieldState.value)) {
                                error = `${validation.label} is required`;
                                isValid = false;
                            } else if (!validatePhone(fieldState.value)) {
                                error = "Please enter a valid phone number";
                                isValid = false;
                            }
                            break;
                    }

                    newState[field].error = error;
                });

                return newState;
            });

            return isValid;
        },
        []
    );

    const resetForm = useCallback(() => {
        setFormState((prev) => {
            const newState = { ...prev };
            Object.keys(newState).forEach((key) => {
                newState[key] = {
                    value: "",
                    touched: false,
                    error: "",
                };
            });
            return newState;
        });
    }, []);

    return {
        formState,
        setValue,
        setTouched,
        validateField,
        validateAll,
        resetForm,
    };
}
